import { useMemo, useState } from "react";

import { useRecoilState, useRecoilValueLoadable } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { getPathFromSectionDataKey } from "../../../containers/FrontEdit/utils";
import { Template } from "../../../models/configuration/definitions/template";
import { KTemplatesTemplatesName } from "../../config/utils";
import {
  CurrentContentEdit,
  frontEditCurrentContentEditsState,
} from "../atoms/frontEditCurrentContentEdits";
import { getDataOfMultipleSectionsSelector } from "../selectors/schema";
import { replaceItemAtIndex } from "../utils";

type UseFrontEditKTemplates = {
  dataOfMultipleSection: any;
  dataOfMultipleSectionLoading: boolean;
  currentContentEdits: CurrentContentEdit[];
  handleReorderSections: (fromId: number, toId: number) => void;
  handleInvertSections: (firstSectionId: number, secondSectionId: number) => void;
  setLocalSectionList: (templates: Template[]) => void;
  localSectionList: Template[];
};

export default function useFrontEditKTemplates(templatesLocation: {
  templatesPage: CONFIGURATION_SECTION_PATH;
  templatesName: KTemplatesTemplatesName;
}): UseFrontEditKTemplates {
  const [currentContentEdits, setCurrentContentEdits] = useRecoilState(
    frontEditCurrentContentEditsState,
  );

  const [localSectionList, setLocalSectionList] = useState<Template[]>([]);

  const dataOfMultipleSectionQuery = useRecoilValueLoadable(
    getDataOfMultipleSectionsSelector({
      keys: [{ configSection: templatesLocation.templatesPage }],
    }),
  );

  const dataOfMultipleSection = useMemo(
    () =>
      dataOfMultipleSectionQuery.state === "loading" ? [] : dataOfMultipleSectionQuery.contents,
    [dataOfMultipleSectionQuery.contents, dataOfMultipleSectionQuery.state],
  );

  const updateCurrentContentEdit = (newSectionList: Template[]) => {
    if (dataOfMultipleSection && dataOfMultipleSection.length !== 0) {
      const dataOfFirstSection = dataOfMultipleSection[0];
      const endpoint = getPathFromSectionDataKey(dataOfFirstSection.key);

      const newContentEdit: CurrentContentEdit = {
        key: dataOfFirstSection.key,
        endpoint: endpoint,
        data: {
          [templatesLocation.templatesName]: newSectionList,
        },
        jsonSchema: dataOfFirstSection.jsonSchema,
        jsonUiSchema: dataOfFirstSection.jsonUiSchema,
        invalidForm: false,
      };

      setCurrentContentEdits((currentContentEdits) => {
        const index = currentContentEdits.findIndex(
          (currentContentEdit) => currentContentEdit.endpoint === newContentEdit.endpoint,
        );
        if (index === -1) {
          return [...currentContentEdits, newContentEdit];
        } else {
          return replaceItemAtIndex(currentContentEdits, index, newContentEdit);
        }
      });
    }
  };

  const handleReorderSections = (fromId, toId) => {
    const clonedSectionList = [...localSectionList];

    if (fromId > toId) {
      const element = clonedSectionList[fromId];
      for (const [i] of clonedSectionList.entries()) {
        if (i > toId && i <= fromId) {
          clonedSectionList[i] = clonedSectionList[i - 1];
        }
        if (i > fromId) {
          break;
        }
      }
      clonedSectionList[toId] = element;
    } else {
      const element = clonedSectionList[fromId];
      for (const [i] of clonedSectionList.entries()) {
        if (i >= fromId && i < toId) {
          clonedSectionList[i] = clonedSectionList[i + 1];
        }
        if (i >= toId) {
          break;
        }
      }
      clonedSectionList[toId] = element;
    }
    updateCurrentContentEdit(clonedSectionList);
  };

  const handleInvertSections = (firstSectionId: number, secondSectionId: number) => {
    const firstSection = localSectionList[firstSectionId];
    const tempSectionList = replaceItemAtIndex(
      localSectionList,
      firstSectionId,
      localSectionList[secondSectionId],
    );
    const invertedSectionList = replaceItemAtIndex(tempSectionList, secondSectionId, firstSection);
    updateCurrentContentEdit(invertedSectionList);
  };

  return {
    dataOfMultipleSection,
    dataOfMultipleSectionLoading: dataOfMultipleSectionQuery.state === "loading",
    currentContentEdits,
    handleReorderSections,
    handleInvertSections,
    setLocalSectionList,
    localSectionList,
  };
}
